import { useState, useEffect, useCallback, useRef } from 'react'
import { useApi } from '../../../hooks/useApi'
import { AutoCompleterConfig, AutoCompleterState } from './types'
import { ApiResponse } from '../../../typings/ApiResponse'
import { apiCall } from '../../../config/api'

export function useAutoCompleter<T>(config: AutoCompleterConfig<T>) {
  const { callApi } = useApi<ApiResponse<T>>()
  
  const [state, setState] = useState<AutoCompleterState<T>>({
    items: [],
    loading: false,
    error: null,
    searchText: '',
    selectedItem: null,
    isOpen: false
  })

  const debounceRef = useRef<NodeJS.Timeout>()

  const searchItems = useCallback(async (searchText: string) => {
    if (!searchText || typeof searchText !== 'string' || searchText.length < (config.minSearchLength || 2)) {
      setState(prev => ({ ...prev, items: [], loading: false, error: null }))
      return
    }

    setState(prev => ({ ...prev, loading: true, error: null }))

    try {
      const searchParams = new URLSearchParams({
        [config.searchParam]: searchText,
        page: '0',
        size: (config.pageSize || 10).toString()
      })

      const url = `${config.searchEndpoint}?${searchParams.toString()}`
      
      // Usar callApi con apiCall como en el patrón del código
      const response = await callApi(apiCall('GET', url))
      
      setState(prev => ({
        ...prev,
        items: response.items || [],
        loading: false,
        error: null
      }))
    } catch (error) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Error en la búsqueda'
      }))
    }
  }, [config, callApi])

  const debouncedSearch = useCallback((searchText: string) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    debounceRef.current = setTimeout(() => {
      searchItems(searchText)
    }, config.debounceMs || 300)
  }, [searchItems, config.debounceMs])

  const handleSearchChange = useCallback((searchText: string) => {
    const safeSearchText = searchText || ''
    setState(prev => ({ ...prev, searchText: safeSearchText }))
    debouncedSearch(safeSearchText)
  }, [debouncedSearch])

  const handleItemSelect = useCallback((item: T | null) => {
    setState(prev => ({
      ...prev,
      selectedItem: item,
      searchText: item ? String(item[config.displayField] || '') : '',
      isOpen: false
    }))
  }, [config.displayField])

  const handleOpen = useCallback(() => {
    setState(prev => ({ ...prev, isOpen: true }))
  }, [])

  const handleClose = useCallback(() => {
    setState(prev => ({ ...prev, isOpen: false }))
  }, [])

  const clearSelection = useCallback(() => {
    setState(prev => ({
      ...prev,
      selectedItem: null,
      searchText: '',
      items: []
    }))
  }, [])

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [])

  return {
    ...state,
    handleSearchChange,
    handleItemSelect,
    handleOpen,
    handleClose,
    clearSelection
  }
}
